/**
 * 对应 SpringMVC 和 Element el-table 的工具类
 * 依赖 moment.js
 */
export class DataTable {
  vue = null;
  storePrefix = null;

  // 排序内容
  sort = null;

  //  el-table 需要的参数
  data = []; // 数据
  multipleSelection = []; // 多选的数组
  size = "mini"; // 大小样式

  // 汇总行的数据（针对MyBatis）
  sumData = null; // 汇总数据

  //  el-pagination 需要的参数
  pageSize = 10; // 分页大小
  currentPage = 1; // 当前分页
  totalElements = 0; // 总记录数
  pageSizes = [10, 20, 30, 40]; // 分页方式
  layout = "total, sizes, prev, pager, next, jumper"; // 默认呈现样式

  //  与后台交互需要的参数
  queryPostUrl = null; // 查询的POST路径
  filters = null; // 按照SpringMVC分页方法要求根据页面的过滤设置拼装过滤数组
  extnedsParms = null; // 额外的查询参数

  // 过滤属性绑定， 内部对象 {columnName: xxx, dslName: xxx, columnType: string,number,date,comparestring,comparestringMin,comparestringMax,dateStart,dateEnd,dateEndWithTime,enum之一, vuePropName: vvv, ext:枚举全路径名等}
  bindFields = []; // 绑定的过滤字段

  /**
   * 获取 storeKey 的内部静态方法
   * @param queryPostUrl【必填】查询的POST路径
   * @param storePrefix 【可选】存储前缀
   * @private
   */
  static _getKey(queryPostUrl, storePrefix) {
    let key = null;
    if (storePrefix) {
      key = storePrefix + "_" + queryPostUrl;
    } else {
      key = queryPostUrl;
    }
    return key;
  }

  /**
   * 工厂方法
   * 尝试从 store 中获取已有的对象， 若没有就创建一个
   * @param vue 【必填】 VUE对象
   * @param queryPostUrl  【必填】查询的POST路径
   * @param storePrefix 存储前缀，默认为 null
   * @param filters  按照SpringMVC分页方法要求根据页面的过滤设置拼装过滤数组的方法， 默认为 null， 即不过滤
   * @param extnedsParms  额外的查询参数， 默认为 null
   * @param pageSize  分页大小， 默认为 10
   * @param currentPage 当前分页， 默认为 1
   */
  static getInstance(vue, queryPostUrl, storePrefix, filters, extnedsParms, pageSize, currentPage) {
    let dt = vue.$store.state.dataTables[DataTable._getKey(queryPostUrl, storePrefix)];
    if (!dt) {
      dt = new DataTable(vue, queryPostUrl, storePrefix, filters, extnedsParms, pageSize, currentPage);
    } else {
      dt.vue = vue;
      //  清掉旧有排序
      dt.sort = null;
      if (filters) {
        dt.filters = filters;
      }
      if (extnedsParms) {
        dt.extnedsParms = extnedsParms;
      } else {
        dt.extnedsParms = null;
      }
    }
    return dt;
  }

  /**
   * 构造器
   * @param vue 【必填】 VUE对象
   * @param queryPostUrl  【必填】查询的POST路径
   * @param storePrefix 存储前缀，默认为 null
   * @param filters  按照SpringMVC分页方法要求根据页面的过滤设置拼装过滤数组的方法， 默认为 null， 即不过滤
   * @param extnedsParms  额外的查询参数， 默认为 null
   * @param pageSize  分页大小， 默认为 10
   * @param currentPage 当前分页， 默认为 1
   */
  constructor(vue, queryPostUrl, storePrefix, filters, extnedsParms, pageSize, currentPage) {
    this.vue = vue;
    this.queryPostUrl = queryPostUrl;
    if (storePrefix) {
      this.storePrefix = storePrefix;
    }
    if (filters) {
      this.filters = filters;
    }
    if (extnedsParms) {
      this.extnedsParms = extnedsParms;
    }
    if (pageSize) {
      this.pageSize = pageSize;
    }
    if (currentPage) {
      this.currentPage = currentPage;
    }

    /**
     * 分页大小改变
     * 供 el-pagination 的 size-change 属性使用
     * @param size
     */
    this.handleSizeChange = (size) => {
      this.pageSize = size;
      this.queryPageData();
    }

    /**
     * 供 el-pagination 的 current-change 属性使用
     * 当前分页改变
     * @param cp
     */
    this.handleCurrentChange = (cp) => {
      this.currentPage = cp;
      this.queryPageData();
    }

    /**
     * 排序改变
     * 供 el-table 的 sort-change 属性使用
     */
    this.handleSortChange = (sv) => {
      if (sv.order) {
        let direction = (sv.order === 'descending' ? ',DESC' : '');
        this.queryPageData(sv.prop + direction);
      }
    }

    /**
     * 选择记录
     * 供 el-table 的 selection-change 属性使用
     */
    this.handleSelectionChange = (val) => {
      this.multipleSelection = val;
    }

    /**
     * 清空绑定的字段
     */
    this.cleanBindFields = () => {
      this.bindFields = [];
    }

    /**
     * 查找绑定字段 (常规字段版本： 不含comparestringMin、 comparestringMax、 dateStart 和 dateEnd、dateEndWithTime 类型)
     * @param columnName
     * @returns {*}
     */
    this.findBindField = (columnName) => {
      if (this.bindFields) {
        for (let i = 0; i < this.bindFields.length; i++) {
          let bindField = this.bindFields[i];
          if (bindField.columnName === columnName && bindField.columnType !== "comparestringMin" && bindField.columnType !== "comparestringMax" &&
            bindField.columnType !== "dateStart" && bindField.columnType !== "dateEnd" && bindField.columnType !== "dateEndWithTime") {
            return bindField;
          }
        }
      }
      return null;
    }

    /**
     * 查找绑定字段
     * @param columnName
     * @returns {*}
     */
    this.findBindFieldByType = (columnName, columnType) => {
      if (this.bindFields) {
        for (let i = 0; i < this.bindFields.length; i++) {
          let bindField = this.bindFields[i];
          if (bindField.columnName === columnName && bindField.columnType === columnType) {
            return bindField;
          }
        }
      }
      return null;
    }

    /**
     * 绑定字符串字段
     */
    this.bindStringField = (columnName, vuePropName, dslName) => {
      let field = this.findBindField(columnName);
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "string";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定数字字段
     */
    this.bindNumberField = (columnName, vuePropName, dslName) => {
      let field = this.findBindField(columnName);
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "number";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定日期字段
     * (vue的属性应该使用 el-date-picker 并设置 value-format="yyyy-MM-dd")
     */
    this.bindDateField = (columnName, vuePropName, dslName) => {
      let field = this.findBindField(columnName);
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "date";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定开始日期字段
     * (vue的属性应该使用 el-date-picker 并设置 value-format="yyyy-MM-dd")
     */
    this.bindDateStartField = (columnName, vuePropName, dslName) => {
      let field = this.findBindFieldByType(columnName, "dateStart");
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "dateStart";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定结束日期字段
     * (vue的属性应该使用 el-date-picker 并设置 value-format="yyyy-MM-dd")
     */
    this.bindDateEndField = (columnName, vuePropName, dslName) => {
      let field = this.findBindFieldByType(columnName, "dateEnd");
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "dateEnd";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定结束日期字段
     * (vue的属性应该使用 el-date-picker 并设置 value-format="yyyy-MM-dd")
     * 注意：为了支持时间，真正查询时会后挪到下一天的零点
     */
    this.bindDateEndWithTimeField = (columnName, vuePropName, dslName) => {
      let field = this.findBindFieldByType(columnName, "dateEndWithTime");
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "dateEndWithTime";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定比较字符串字段
     * (若日期 vue的属性应该使用 el-date-picker 并设置 value-format="yyyy-MM-dd")
     */
    this.bindcomparestringField = (columnName, vuePropName, dslName) => {
      let field = this.findBindField(columnName);
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "comparestring";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定比较字符串开始字段
     * (若日期 vue的属性应该使用 el-date-picker 并设置 value-format="yyyy-MM-dd")
     */
    this.bindComparestringMinField = (columnName, vuePropName, dslName) => {
      let field = this.findBindFieldByType(columnName, "dateStart");
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "comparestringMin";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定比较字符串结束字段
     * (若日期 vue的属性应该使用 el-date-picker 并设置 value-format="yyyy-MM-dd")
     */
    this.bindComparestringMaxField = (columnName, vuePropName, dslName) => {
      let field = this.findBindFieldByType(columnName, "dateEnd");
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "comparestringMax";
      field.vuePropName = vuePropName;
      field.ext = null;
      field.dslName = dslName;
    }

    /**
     * 绑定枚举字段
     */
    this.bindEnumField = (columnName, vuePropName, enumFullName, dslName) => {
      let field = this.findBindField(columnName);
      if (!field) {
        field = {};
        this.bindFields.push(field);
      }
      field.columnName = columnName;
      field.columnType = "enum";
      field.vuePropName = vuePropName;
      field.ext = enumFullName;
      field.dslName = dslName;
    }

    /**
     * 把绑定的字段 转换成 过滤属性
     */
    this.bindFieldToFilter = () => {
      this.filters = [];
      if (this.bindFields) {
        for (let i = 0; i < this.bindFields.length; i++) {
          try {
            let bindField = this.bindFields[i];
            let propValue = this.vue[bindField.vuePropName];
            if (propValue != null && propValue !== "") {
              let bindField = this.bindFields[i];
              let filter = {};
              filter.name = bindField.columnName;
              filter.dslName = bindField.dslName;
              // 日期范围特殊处理
              if (bindField.columnType === "dateStart" || bindField.columnType === "dateEnd" || bindField.columnType === "dateEndWithTime") {
                filter.type = "date";
              } else if (bindField.columnType === "comparestringMin" || bindField.columnType === "comparestringMax") {
                filter.type = "comparestring"
              } else {
                filter.type = bindField.columnType;
              }
              // 字符串类型自动补 %
              if (bindField.columnType === "string") {
                if (propValue.substring(0, 1) !== "%") {
                  propValue = "%" + propValue;
                }
                if (propValue.substring(propValue.length - 1, propValue.length) !== "%") {
                  propValue += "%";
                }
              }
              // 枚举补枚举全路径
              if (bindField.columnType === "enum") {
                filter.enumName = bindField.ext;
              }

              if (bindField.columnType === "dateStart") { // 开始日期
                filter.min = propValue;
              } else if (bindField.columnType === "dateEnd") { // 结束日期
                filter.max = propValue;
              } else if (bindField.columnType === "dateEndWithTime") { // 结束日期，为了处理时间问题，后移一天
                filter.max = this.vue.$moment(propValue, "YYYY-MM-DD").add(1, 'd').format("YYYY-MM-DD");
              } else if (bindField.columnType === "comparestringMin") { // 比较字符串开始
                filter.min = propValue;
              } else if (bindField.columnType === "comparestringMax") { // 比较字符串开始结束
                filter.max = propValue;
              } else { // 后台接口内置字段类型
                filter.value = propValue;
              }
              this.filters.push(filter);
            }
          } catch (e) {
            console.log(e);
          }
        }
      }
    }

    /**
     * 把绑定的字段 还原成 Vue 的属性
     */
    this.bindFieldToVueProp = () => {
      if (this.filters) {
        for (let i = 0; i < this.filters.length; i++) {
          try {
            let filter = this.filters[i];
            let value = filter.value;
            if (value) {
              let bindField = this.findBindField(filter.name);
              if (bindField) {
                if (typeof this.vue[bindField.vuePropName] !== "undefined") {
                  // 去掉字符串类型自动补 %
                  if (bindField.columnType === "string") {
                    if (value.substring(0, 1) === "%") {
                      value = value.substring(1, value.length);
                    }
                    if (value.substring(value.length - 1, value.length) === "%") {
                      value = value.substring(0, value.length - 1);
                    }
                  }
                  // 结束日期前挪一天
                  if (bindField.columnType === 'dateEndWithTime') {
                    value = this.vue.$mement(value).subtract(value, 1).format("YYYY-MM-DD");
                  }
                  this.vue.$data[bindField.vuePropName] = value;
                }
              }
            }
          } catch (e) {
            console.warn(e);
          }
        }
        // 强制刷新UI
        // this.vue.$forceUpdate();
      }
    }

    /**
     * 向后台查询数据
     */
    this.queryPageData = (sort) => {
      //  清掉选中缓存
      this.multipleSelection = []

      let that = this;
      if (sort) {
        this.sort = sort;
      }

      //  默认参数
      let params = {
        page: this.currentPage - 1,
        size: this.pageSize,
        sort: that.sort
      }

      //  额外参数
      if (this.extnedsParms) {
        for (let attr in this.extnedsParms) {
          params[attr] = this.extnedsParms[attr];
        }
      }
      //  把状态保存进 store
      let key = DataTable._getKey(queryPostUrl, storePrefix);
      this.vue.$store.commit('SET_DATA_TABLE', {key: key, dt: this});

      //  从服务器获取列表
      if (this.filters == null || !(this.filters instanceof Array)) {
        this.filters = [];
      }
      //  console.debug(this.filters);
      this.vue.axios.post(this.queryPostUrl, this.filters, {params: params})
        .then(resp => {
          if (resp.data.code === 200) {
            let result = resp.data.data;
            if (result != null) {
              this.data = result.content; // 分页数据
              this.totalElements = result.totalElements; // 总记录数
              if (result.sumData) {
                this.sumData = result.sumData;
              } else {
                this.sumData = null;
              }
            } else {
              this.data = []
              this.totalElements = 0
            }
          } else {
            this.vue.$message({
              type: 'error',
              message: '查询异常' + resp.data.message
            });
          }
        })
        .catch(reason => {
          this.vue.$message({
            type: 'error',
            message: '查询异常' + reason
          });
        });
    }
  }
}



// WEBPACK FOOTER //
// ./src/data-table.js